<template>
  <div>
    <v-data-table
      :headers="headers"
      :items="audits"
      :loading="loading"
      item-key="id"
      no-data-text="Không có đợt kiểm kê nào"
      :server-items-length="totalItems"
      :footer-props="{
        'items-per-page-text': 'Hiển thị mỗi trang',
        'items-per-page-all-text': 'Tất cả',
        'items-per-page-options': [10, 20, 50]
      }"
      @update:options="onOptionsChange"
    >
      <!-- Cột 1: Mã đợt -->
      <template v-slot:item.code="{ item }">
        <a class="font-weight-bold primary--text" @click="onViewDetail(item)">{{ item.code }}</a>
      </template>

      <!-- Cột 2: Tiêu đề đợt kiểm kê -->
      <template v-slot:item.title="{ item }">
        <span>{{ item.title || '-' }}</span>
      </template>

      <!-- Cột 3: Ngày kiểm kê -->
      <template v-slot:item.auditDate="{ item }">
        <span>{{ item.auditDate || '-' }}</span>
      </template>

      <!-- Cột 4: Phạm vi kiểm kê (dạng text, phân cách bởi dấu phẩy) -->
      <template v-slot:item.scope="{ item }">
        <span v-if="getItemScopeTags(item).length">
          {{ getItemScopeTags(item).join(', ') }}
        </span>
        <span v-else class="grey--text">-</span>
      </template>

      <!-- Cột 5: Người thực hiện -->
      <template v-slot:item.auditor="{ item }">
        <span>{{ (item.auditor && (item.auditor.fullName || item.auditor.username || item.auditor)) || (item.createdBy && (item.createdBy.fullName || item.createdBy.username)) || '-' }}</span>
      </template>

      <!-- Cột 6: Người xác nhận -->
      <template v-slot:item.approver="{ item }">
        <span>{{ (item.approver && (item.approver.fullName || item.approver.username || item.approver)) || (item.confirmedBy && (item.confirmedBy.fullName || item.confirmedBy.username)) || (item.manager && (item.manager.fullName || item.manager.username)) || '-' }}</span>
      </template>

      <!-- Cột 7: Trạng thái -->
      <template v-slot:item.status="{ item }">
        <v-chip small :color="getStatusColor(item.status)" dark>
          {{ getStatusLabel(item.status) }}
        </v-chip>
      </template>

      <!-- Cột 8: Thao tác -->
      <template v-slot:item.actions="{ item }">
        <v-btn icon small color="primary" @click="onViewDetail(item)" title="Xem biên bản">
          <v-icon small>mdi-eye</v-icon>
        </v-btn>
        <v-btn icon small color="error" @click="onRemove(item)" title="Xóa đợt kiểm kê" v-if="item.status === 'DRAFT'">
          <v-icon small>mdi-delete</v-icon>
        </v-btn>
      </template>
    </v-data-table>

    <!-- Dialog chi tiết kiểm kê -->
    <AuditDetailDialog :state="detailDialog" :item="selectedItem" @updated="refresh()" />
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import { get } from 'lodash'
import AuditDetailDialog from '@/modules/facility/audit/AuditDetailDialog.vue'

const headers = [
  { text: 'Mã đợt', value: 'code', align: 'left', sortable: false },
  { text: 'Tiêu đề đợt kiểm kê', value: 'title', align: 'left', sortable: false },
  { text: 'Ngày kiểm kê', value: 'auditDate', align: 'center', sortable: false },
  { text: 'Phạm vi kiểm kê', value: 'scope', align: 'left', sortable: false },
  { text: 'Người thực hiện', value: 'auditor', align: 'left', sortable: false },
  { text: 'Người xác nhận', value: 'approver', align: 'left', sortable: false },
  { text: 'Trạng thái', value: 'status', align: 'center', sortable: false },
  { text: 'Thao tác', value: 'actions', align: 'center', sortable: false, width: '100px' }
]

export default {
  name: 'AuditDataTable',
  components: { AuditDetailDialog },
  data() {
    return {
      headers,
      detailDialog: false,
      selectedItem: null
    }
  },
  computed: {
    ...mapState('FacilityAudits', ['audits', 'totalItems', 'pageText', 'loading'])
  },
  methods: {
    get,
    ...mapActions('FacilityAudits', ['requestPageSettings', 'searchAuditList', 'removeAudit']),
    async refresh(query) {
      this.searchAuditList(query || {})
    },
    onOptionsChange({ page, itemsPerPage }) {
      this.requestPageSettings({ page, itemsPerPage })
    },
    getTagColor(tag) {
      const title = String(tag || '').toLowerCase()
      if (title.includes('cấp 1') || title.includes('tiểu học')) return 'indigo'
      if (title.includes('cấp 2') || title.includes('thcs')) return 'teal'
      if (title.includes('cấp 3') || title.includes('thpt')) return 'deep-purple'
      return 'primary'
    },
    getTagFullName(tag) {
      if (!tag) return ''
      const title = String(tag).trim()
      if (title.toLowerCase() === 'cấp 1' || title.toLowerCase().includes('tiểu học')) return `${title} (Tiểu học)`
      if (title.toLowerCase() === 'cấp 2' || title.toLowerCase().includes('thcs'))
        return `${title} (Trung học cơ sở - THCS)`
      if (title.toLowerCase() === 'cấp 3' || title.toLowerCase().includes('thpt'))
        return `${title} (Trung học phổ thông - THPT)`
      return title
    },
    getItemScopeTags(item) {
      if (!item) return []

      // 1. Ưu tiên trích xuất các đầu mục từ danh sách thiết bị kiểm đếm
      if (Array.isArray(item.items) && item.items.length) {
        const catNames = new Set()
        item.items.forEach(it => {
          const name =
            it.categoryName ||
            (it.category && (it.category.name || it.category.title || it.category))
          if (name && name !== '-' && typeof name === 'string' && name.trim()) {
            catNames.add(name.trim())
          }
        })
        if (catNames.size) return Array.from(catNames)
      }

      // 2. Lấy từ scope nếu có
      if (Array.isArray(item.scope) && item.scope.length) {
        return item.scope
          .map(s => (typeof s === 'object' ? s.name || s.title || s : s))
          .filter(Boolean)
      }
      if (typeof item.scope === 'string' && item.scope.trim()) {
        return item.scope.includes(',')
          ? item.scope
              .split(',')
              .map(s => s.trim())
              .filter(Boolean)
          : [item.scope.trim()]
      }

      // 3. Lấy từ category nếu có
      if (item.category) {
        const catName =
          (item.category && (item.category.name || item.category.title)) || item.category
        if (catName && typeof catName === 'string') return [catName.trim()]
      }

      return []
    },
    getStatusLabel(status) {
      switch (status) {
        case 'DRAFT': return 'Bản nháp'
        case 'IN_PROGRESS': return 'Đang kiểm kê'
        case 'COMPLETED': return 'Đã hoàn thành'
        default: return status
      }
    },
    getStatusColor(status) {
      switch (status) {
        case 'DRAFT': return 'blue-grey'
        case 'IN_PROGRESS': return 'primary'
        case 'COMPLETED': return 'success'
        default: return 'grey'
      }
    },
    onViewDetail(item) {
      this.selectedItem = item
      this.detailDialog = !this.detailDialog
    },
    onRemove(item) {
      this.$dialog.confirm({
        title: 'Thông báo xác nhận',
        text: `Bạn có chắc muốn xóa đợt kiểm kê "${item.code}"?`,
        okText: 'Xóa',
        cancelText: 'Hủy',
        done: async () => {
          this.$loading.active = true
          try {
            await this.removeAudit(item.id)
            this.$alert.deleteSuccess()
          } catch (error) {
            this.$alert.deleteError(error)
          } finally {
            this.$loading.active = false
          }
        }
      })
    }
  }
}
</script>
